// src/components/programs/ProgramForm/Step1Sections/DayItinerary/ProductInstanceCard.jsx

import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Chip,
    Grid,
    Collapse,
    alpha,
    useTheme,
    Stack,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    DragIndicator as DragIndicatorIcon,
    ExpandMore as ExpandMoreIcon,
    Business as SupplierIcon,
    RoomService as ServiceIcon,
    // AttachMoney as PriceIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import FormInput from "../../../../inputs/FormInput";
import { formatPrice } from "../../helpers/quotationCalculations";
import useGetAirlinesForSelect from '../../../../../hooks/airlines/useGetAirlinesForSelect';
import useGetAirportsForSelect from '../../../../../hooks/airports/useGetAirportsForSelect';

const ProductInstanceCard = ({
    product,
    dayIndex,
    dayDate,
    productIndex,
    onRemove,
    onUpdateField,
    errors = {},
    isDragging = false,
    isDragOver = false,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
    isArabic,
    activeLang,
    citiesOptions = [],
    loadingCities = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { i18n } = useTranslation();
    const lng = i18n.language;
    const [isExpanded, setIsExpanded] = useState(false);

    // ✅ مهمة 2: State محلي لتتبع الأخطاء المحلية
    const [localErrors, setLocalErrors] = useState({});

    // تحديث الأخطاء المحلية عند تغيير errors من props
    useEffect(() => {
        setLocalErrors(errors);
    }, [errors]);

    // Get pricing unit code
    const pricingCode = product.product_snapshot?.pricing_unit;

    // Check if it's a free day or flight product
    const typeCode = product.product_snapshot?.productType?.code || product.product_snapshot?.productType ||
        product.product_snapshot?.product_type?.code || product.product_snapshot?.product_type ||
        product.productType?.code || product.productType ||
        product.product_type?.code || product.product_type;
    const isFreeDay = typeCode === 'free_day' || typeCode === 'free_time';
    const isFlight = typeCode === 'flight';


    // ✅ مهمة 6: الحصول على city_id من product_snapshot.city (من المنتج الأصلي)
    const getCityIdFromSnapshot = () => {
        const cityData = product.product_snapshot?.city;
        if (!cityData) return null;

        // إذا كانت city عبارة عن object مع id
        if (typeof cityData === 'object' && cityData !== null && cityData.id) {
            return cityData.id;
        }

        // إذا كانت city عبارة عن id مباشر
        if (typeof cityData === 'number') {
            return cityData;
        }

        return null;
    };

    const cityIdFromProduct = getCityIdFromSnapshot();

    const { airlinesOptions, isLoading: loadingAirlines } = useGetAirlinesForSelect();
    const { airports, isLoading: loadingAirports } = useGetAirportsForSelect();



    const sortedAirlinesOptions = React.useMemo(() => {
        const selectedId = product.dynamic_fields?.flight_details?.airline_id;
        if (!selectedId || !Array.isArray(airlinesOptions)) return airlinesOptions;

        const selectedOption = airlinesOptions.find(option => String(option.id) === String(selectedId));
        if (!selectedOption) return airlinesOptions;

        return [selectedOption, ...airlinesOptions.filter(option => String(option.id) !== String(selectedId))];
    }, [airlinesOptions, product.dynamic_fields?.flight_details?.airline_id]);

    const getAirportOptions = (field) => {
        const options = airports || [];
        const selectedId = product.dynamic_fields?.flight_details?.[field];
        if (!selectedId || !Array.isArray(options)) return options;

        const selectedOption = options.find(option => String(option.id) === String(selectedId));
        if (!selectedOption) return options;

        return [selectedOption, ...options.filter(option => String(option.id) !== String(selectedId))];
    };

    // ✅ مهمة 2: دالة لإخفاء الخطأ فوراً عند التعديل
    const clearFieldError = (fieldName) => {
        setLocalErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    };

    // Handle dynamic field change
    const handleDynamicFieldChange = (field, value) => {
        if (onUpdateField) {
            onUpdateField(`dynamic_fields.${field}`, value);
            // ✅ مهمة 2: إخفاء الخطأ فوراً عند التغيير
            clearFieldError(`${product.instance_id}_${field}`);
        }
    };

    const handleFlightDetailChange = (field, value) => {
        if (onUpdateField) {
            onUpdateField(`dynamic_fields.flight_details.${field}`, value);
            clearFieldError(`${product.instance_id}_${field}`);
        }
    };



    // ✅ منع drag and drop عندما تكون الماوس فوق input
    const handleInputPointerDown = (e) => {
        e.stopPropagation();
    };

    const handleInputChange = (e) => {
        e.stopPropagation();
    };

    // ✅ مهمة 4: التحقق من البيانات الناقصة
    const hasIncompleteData = () => {
        if (isFreeDay) return false; // Free day has no required fields
        if (isFlight) {
            const flightDetails = product.dynamic_fields?.flight_details || {};
            return !flightDetails.airline_id || !flightDetails.departure_airport_id || !flightDetails.arrival_airport_id ||
                !flightDetails.departure_date || !flightDetails.arrival_date || !flightDetails.departure_time ||
                !flightDetails.arrival_time || !flightDetails.flight_number || !flightDetails.pnr ||
                flightDetails.baggage_weight === null || flightDetails.baggage_weight === undefined || flightDetails.baggage_weight === "";
        }

        if (pricingCode === "per_person") {
            return !product.dynamic_fields?.pickup_time || !product.dynamic_fields?.city_id;
        } else if (pricingCode === "per_night") {
            // nights can be 0 — consider incomplete only when null/undefined/empty
            return (
                product.dynamic_fields?.nights === null ||
                product.dynamic_fields?.nights === undefined ||
                product.dynamic_fields?.nights === ""
            );
        } else if (pricingCode === "per_unit") {
            return !product.dynamic_fields?.time || !product.dynamic_fields?.units;
        }
        return false;
    };

    // Get pricing type label
    const getPricingTypeLabel = () => {
        switch (pricingCode) {
            case "per_person":
                return lng === "ar" ? "👤 للشخص" : "👤 Per Person";
            case "per_night":
                return lng === "ar" ? "🌙 لليلة" : "🌙 Per Night";
            case "per_unit":
                return lng === "ar" ? "📦 للوحدة" : "📦 Per Unit";
            case "free_time":
                return lng === "ar" ? "وقت مجاني" : "Free Time";
            case "free_day":
                return lng === "ar" ? "اليوم المجاني" : "Free Day";
            case "per_person_night":
                return lng === "ar" ? "🏨 فندق/شخص/ليلة" : "🏨 Per Person/Night";
            default:
                return lng === "ar" ? "غير محدد" : "Unknown";
        }
    };


    // ✅ مهمة 6: تهيئة city_id عند إضافة المنتج إذا كانت فارغة
    useEffect(() => {
        if (pricingCode === "per_person" && cityIdFromProduct && !product.dynamic_fields?.city_id) {
            handleDynamicFieldChange("city_id", cityIdFromProduct);
        }
        if (
            pricingCode === "per_unit" &&
            (product.dynamic_fields?.units === null ||
                product.dynamic_fields?.units === undefined ||
                product.dynamic_fields?.units === "")
        ) {
            handleDynamicFieldChange("units", 1);
        }
        if (
            pricingCode === "per_night" &&
            (product.dynamic_fields?.nights === null ||
                product.dynamic_fields?.nights === undefined ||
                product.dynamic_fields?.nights === "")
        ) {
            handleDynamicFieldChange("nights", 1);
        }
        if (
            isFlight &&
            dayDate &&
            !product.dynamic_fields?.flight_details?.departure_date
        ) {
            handleFlightDetailChange("departure_date", dayDate);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const sortedCitiesOptions = React.useMemo(() => {
        const selectedId = product.dynamic_fields?.city_id;
        if (!selectedId || !Array.isArray(citiesOptions)) return citiesOptions;

        const selectedOption = citiesOptions.find(option => String(option.id) === String(selectedId));
        if (!selectedOption) return citiesOptions;

        return [selectedOption, ...citiesOptions.filter(option => String(option.id) !== String(selectedId))];
    }, [citiesOptions, product.dynamic_fields?.city_id]);

    // Render compact dynamic fields
    const renderDynamicFields = () => {
        if (isFreeDay) return null; // No dynamic fields for free day

        if (isFlight) {
            const flight = product.dynamic_fields?.flight_details || {};
            return (
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "شركة الطيران" : "Airline"}
                            type="select"
                            name={`${product.instance_id}_airline_id`}
                            value={flight.airline_id || ""}
                            onChange={(e) => handleFlightDetailChange("airline_id", e.target.value)}
                            options={[{ id: 'all', name: lng === 'ar' ? 'اختر شركة الطيران' : 'Select Airline' }, ...sortedAirlinesOptions]}
                            loading={loadingAirlines}
                            error={!!localErrors[`${product.instance_id}_airline_id`]}
                            helperText={localErrors[`${product.instance_id}_airline_id`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "مطار المغادرة" : "Departure Airport"}
                            type="select"
                            name={`${product.instance_id}_departure_airport_id`}
                            value={flight.departure_airport_id || ""}
                            onChange={(e) => handleFlightDetailChange("departure_airport_id", e.target.value)}
                            options={[{ id: 'all', name: lng === 'ar' ? 'اختر مطار المغادرة' : 'Select Departure Airport' }, ...getAirportOptions('departure_airport_id')]}
                            loading={loadingAirports}
                            error={!!localErrors[`${product.instance_id}_departure_airport_id`]}
                            helperText={localErrors[`${product.instance_id}_departure_airport_id`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "مطار الوصول" : "Arrival Airport"}
                            type="select"
                            name={`${product.instance_id}_arrival_airport_id`}
                            value={flight.arrival_airport_id || ""}
                            onChange={(e) => handleFlightDetailChange("arrival_airport_id", e.target.value)}
                            options={[{ id: 'all', name: lng === 'ar' ? 'اختر مطار الوصول' : 'Select Arrival Airport' }, ...getAirportOptions('arrival_airport_id')]}
                            loading={loadingAirports}
                            error={!!localErrors[`${product.instance_id}_arrival_airport_id`]}
                            helperText={localErrors[`${product.instance_id}_arrival_airport_id`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "رقم الرحلة" : "Flight Number"}
                            name={`${product.instance_id}_flight_number`}
                            value={flight.flight_number || ""}
                            onChange={(e) =>
                                handleFlightDetailChange(
                                    "flight_number",
                                    e.target.value.toUpperCase()
                                )
                            } error={!!localErrors[`${product.instance_id}_flight_number`]}
                            helperText={localErrors[`${product.instance_id}_flight_number`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "تاريخ المغادرة" : "Departure Date"}
                            type="date"
                            name={`${product.instance_id}_departure_date`}
                            value={flight.departure_date || ""}
                            onChange={(e) => handleFlightDetailChange("departure_date", e.target.value)}
                            error={!!localErrors[`${product.instance_id}_departure_date`]}
                            helperText={localErrors[`${product.instance_id}_departure_date`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "تاريخ الوصول" : "Arrival Date"}
                            type="date"
                            name={`${product.instance_id}_arrival_date`}
                            value={flight.arrival_date || ""}
                            onChange={(e) => handleFlightDetailChange("arrival_date", e.target.value)}
                            error={!!localErrors[`${product.instance_id}_arrival_date`]}
                            helperText={localErrors[`${product.instance_id}_arrival_date`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "وقت المغادرة" : "Departure Time"}
                            type="time"
                            name={`${product.instance_id}_departure_time`}
                            value={flight.departure_time || ""}
                            onChange={(e) => handleFlightDetailChange("departure_time", e.target.value)}
                            error={!!localErrors[`${product.instance_id}_departure_time`]}
                            helperText={localErrors[`${product.instance_id}_departure_time`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "وقت الوصول" : "Arrival Time"}
                            type="time"
                            name={`${product.instance_id}_arrival_time`}
                            value={flight.arrival_time || ""}
                            onChange={(e) => handleFlightDetailChange("arrival_time", e.target.value)}
                            error={!!localErrors[`${product.instance_id}_arrival_time`]}
                            helperText={localErrors[`${product.instance_id}_arrival_time`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "PNR" : "PNR"}
                            name={`${product.instance_id}_pnr`}
                            value={flight.pnr || ""}
                            onChange={(e) =>
                                handleFlightDetailChange(
                                    "pnr",
                                    e.target.value.toUpperCase()
                                )
                            } error={!!localErrors[`${product.instance_id}_pnr`]}
                            helperText={localErrors[`${product.instance_id}_pnr`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "وحدة الأمتعة" : "Baggage Unit"}
                            type="select"
                            name={`${product.instance_id}_baggage_unit`}
                            value={flight.baggage_unit || "kg"}
                            onChange={(e) => handleFlightDetailChange("baggage_unit", e.target.value)}
                            options={[
                                { id: "kg", name: lng === "ar" ? "كيلوجرام" : "Kilogram (kg)" },
                                { id: "pieces", name: lng === "ar" ? "قطع" : "Pieces" },
                            ]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "وزن الأمتعة" : "Baggage Weight"}
                            type="number "
                            name={`${product.instance_id}_baggage_weight`}
                            placeholder={lng === "ar" ? "أدخل وزن الأمتعة" : "Enter baggage weight"}
                            value={flight.baggage_weight ?? ""}
                            onChange={(e) => handleFlightDetailChange("baggage_weight", e.target.value || null)}
                            error={!!localErrors[`${product.instance_id}_baggage_weight`]}
                            helperText={localErrors[`${product.instance_id}_baggage_weight`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                </Grid>
            );
        }
        if (pricingCode === "per_person" && !isFlight) {
            return (
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "وقت الاستلام" : "Pickup Time"}
                            type="time"
                            name={`${product.instance_id}_pickup_time`}
                            value={product.dynamic_fields?.pickup_time || ""}
                            onChange={(e) => handleDynamicFieldChange("pickup_time", e.target.value)}
                            error={!!localErrors[`${product.instance_id}_pickup_time`]}
                            helperText={localErrors[`${product.instance_id}_pickup_time`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {/* ✅ مهمة 6: city_id من المنتج مباشرة، dropdown من cities */}
                        <FormInput
                            label={lng === "ar" ? "المدينة" : "City"}
                            type="select"
                            name={`${product.instance_id}_city_id`}
                            value={product.dynamic_fields?.city_id || ""}
                            onChange={(e) => handleDynamicFieldChange("city_id", e.target.value)}
                            options={sortedCitiesOptions}
                            loading={loadingCities}
                            error={!!localErrors[`${product.instance_id}_city_id`]}
                            helperText={localErrors[`${product.instance_id}_city_id`] || (lng === "ar" ? "تلقائياً من المنتج" : "Auto from product")}
                            required={true}
                            size="small"
                        />
                    </Grid>
                </Grid>
            );
        } else if (pricingCode === "per_unit") {
            return (
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "الوقت" : "Time"}
                            type="time"
                            name={`${product.instance_id}_time`}
                            value={product.dynamic_fields?.time || ""}
                            onChange={(e) => handleDynamicFieldChange("time", e.target.value)}
                            error={!!localErrors[`${product.instance_id}_time`]}
                            helperText={localErrors[`${product.instance_id}_time`]}
                            required={true}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label={lng === "ar" ? "عدد الوحدات" : "Number of Units"}
                            type="number"
                            name={`${product.instance_id}_units`}
                            placeholder={lng === "ar" ? "أدخل عدد الوحدات" : "Enter number of units"}
                            value={product.dynamic_fields?.units ?? ""}
                            onChange={(e) => {
                                const value = e.target.value;
                                handleDynamicFieldChange("units", value ? parseInt(value) : null);
                            }}
                            error={!!localErrors[`${product.instance_id}_units`]}
                            helperText={localErrors[`${product.instance_id}_units`]}
                            required={true}
                            inputProps={{ min: 1 }}
                            size="small"
                        />
                    </Grid>
                </Grid>
            );
        } else if (pricingCode === "per_night") {
            return (
                <FormInput
                    label={lng === "ar" ? "عدد الليالي" : "Number of Nights"}
                    type="number"
                    name={`${product.instance_id}_nights`}
                    placeholder={lng === "ar" ? "أدخل عدد الليالي" : "Enter number of nights"}
                    value={product.dynamic_fields?.nights ?? ""}
                    onChange={(e) => {
                        const value = e.target.value;
                        handleDynamicFieldChange("nights", value === "" ? null : parseInt(value));
                    }}
                    error={!!localErrors[`${product.instance_id}_nights`]}
                    helperText={localErrors[`${product.instance_id}_nights`]}
                    required={true}
                    inputProps={{ min: 0 }}
                    size="small"
                />
            );
        }

        return null;
    };

    // Check if has dynamic fields
    const hasDynamicFields = (isFlight || (pricingCode && ['per_person', 'per_night', 'per_unit'].includes(pricingCode))) && !isFreeDay;

    // ✅ مهمة 4: تحديد لون Border
    const isIncomplete = hasIncompleteData();

    // ✅ Handler للـ drag start - منع drag من inputs وتمرير البيانات بشكل صحيح
    const handleCardDragStart = (e) => {
        const target = e.target;
        // منع drag من inputs
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            e.preventDefault();
            return;
        }
        const closestInput = target.closest('input, textarea');
        if (closestInput) {
            e.preventDefault();
            return;
        }
        // ✅ استدعاء الـ parent handler مع ضمان وجود dataTransfer
        if (onDragStart) {
            // التأكد من وضع البيانات الصحيحة (text/plain with internal-XX-XX)
            e.stopPropagation();
            onDragStart(e);
        }
    };

    // ✅ معالجات drag ONLY للـ internal reordering
    // ننتظر البيانات: text/plain مع internal-${dayIndex}-${index}
    const handleCardDragEnter = (e) => {
        const plainData = e.dataTransfer.getData('text/plain');
        // const jsonData = e.dataTransfer.getData('application/json');

        // إذا كانت بيانات خارجية (منتج جديد من الـ panel) تترك pass through للـ parent
        if (!plainData || !plainData.startsWith('internal-')) {
            // دع الـ event تمر للـ parent container
            return;
        }

        // فقط للـ internal reorder - منع التمرير للـ parent
        e.preventDefault();
        e.stopPropagation();
        if (onDragOver) onDragOver(e);
    };

    const handleCardDragOver = (e) => {
        const plainData = e.dataTransfer.getData('text/plain');

        // إذا لم تكن بيانات internal - اترك تمر للـ parent
        if (!plainData || !plainData.startsWith('internal-')) {
            return;
        }

        // فقط للـ internal reorder
        e.preventDefault();
        e.stopPropagation();
        if (onDragOver) onDragOver(e);
    };

    const handleCardDrop = (e) => {
        const plainData = e.dataTransfer.getData('text/plain');

        // إذا لم تكن بيانات internal - اترك تمر للـ parent
        if (!plainData || !plainData.startsWith('internal-')) {
            return;
        }

        // فقط للـ internal reorder
        e.preventDefault();
        e.stopPropagation();
        if (onDrop) onDrop(e);
    };

    return (
        <Card
            onDragEnter={handleCardDragEnter}
            onDragOver={handleCardDragOver}
            onDrop={handleCardDrop}
            elevation={isDragging ? 4 : 0}
            sx={{
                // ✅ مهمة 4: Border أحمر للبيانات الناقصة
                border: isIncomplete
                    ? `2px solid ${theme.palette.error.main}`
                    : isDragOver
                        ? `1.5px solid ${theme.palette.primary.main}`
                        : `1.5px solid ${alpha(isDark ? '#FFFFFF' : '#000000', 0.08)}`,
                borderRadius: 2,
                opacity: isDragging ? 0.5 : 1,
                transform: isDragOver ? 'scale(1.01)' : 'scale(1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: 2,
                    borderColor: isIncomplete
                        ? theme.palette.error.main
                        : alpha(theme.palette.primary.main, 0.4),
                },
                bgcolor: isDark ? alpha('#FFFFFF', 0.02) : '#FFFFFF',
                userSelect: 'auto',
            }}
        >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Compact Header */}
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: hasDynamicFields || product.selected_supplier ? 1.5 : 0 }}>
                    {/* Drag Handle - Wrapper للسحب */}
                    <Box
                        draggable
                        onDragStart={handleCardDragStart}
                        onDragEnd={onDragEnd}
                        sx={{
                            cursor: "grab",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            userSelect: 'none',
                            '&:active': {
                                cursor: 'grabbing',
                            }
                        }}
                    >
                        <DragIndicatorIcon
                            sx={{
                                color: "text.secondary",
                                fontSize: 20,
                                opacity: 0.4,
                            }}
                        />
                    </Box>

                    {/* Product Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                                fontSize: '0.9rem',
                                lineHeight: 1.3,
                                mb: 0.75,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {lng === "ar"
                                ? product.product_snapshot?.title?.ar
                                : product.product_snapshot?.title?.en}
                        </Typography>

                        {/* Compact Tags */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {/* Pricing Type */}
                            <Chip
                                label={getPricingTypeLabel()}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.info.main, 0.08),
                                    color: 'info.main',
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                                }}
                            />

                            {/* Category */}
                            {product.product_snapshot?.category && (
                                <Chip
                                    label={
                                        lng === "ar"
                                            ? product.product_snapshot.category.name?.ar
                                            : product.product_snapshot.category.name?.en
                                    }
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        color: 'primary.main',
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                    }}
                                />
                            )}

                            {/* Price Badge */}
                            {!product.pricing?.price || (
                                <Chip
                                    // icon={<PriceIcon style={{ fontSize: 14 }} />}
                                    label={`฿ ${formatPrice(product.pricing.custom_price || product.pricing.price)}`}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        bgcolor: alpha(theme.palette.success.main, 0.08),
                                        color: 'success.main',
                                        border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                                        '& .MuiChip-icon': {
                                            fontSize: 14,
                                            ml: 0.5,
                                        },
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>

                    {/* ✅ مهمة 5: تغيير مكان أيقونة الحذف - الآن في الأعلى بجانب Expand */}
                    <Stack direction="row" spacing={3.5} alignItems="center">
                        {/* Delete Button - الآن في الأعلى */}
                        <IconButton
                            onClick={onRemove}
                            size="small"
                            sx={{
                                width: 28,
                                height: 28,
                                color: 'error.main',
                                bgcolor: alpha(theme.palette.error.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.error.main, 0.15),
                                    borderColor: theme.palette.error.main,
                                }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>

                        {/* Expand Button */}
                        {(hasDynamicFields || product.selected_supplier || product.selected_service) && (
                            <IconButton
                                size="small"
                                onClick={() => setIsExpanded(!isExpanded)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            >
                                <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>

                {/* Collapsible Content */}
                <Collapse in={isExpanded} timeout="auto">
                    <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                        {/* Dynamic Fields */}
                        {hasDynamicFields && (
                            <Box
                                sx={{ mb: product.selected_supplier || product.selected_service ? 1.5 : 0 }}
                                onPointerDown={handleInputPointerDown}
                                onPointerUp={handleInputPointerDown}
                                onChange={handleInputChange}
                            >
                                {renderDynamicFields()}
                            </Box>
                        )}

                        {/* Supplier & Service - Compact */}
                        {(product.selected_supplier || product.selected_service) && !isFreeDay && (
                            <Stack spacing={0.75}>
                                {product.selected_supplier && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1,
                                            borderRadius: 1.5,
                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1,
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <SupplierIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                                                {lng === "ar" ? "المورد" : "Supplier"}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                                                {lng === "ar"
                                                    ? product.selected_supplier.name?.ar
                                                    : product.selected_supplier.name?.en}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {product.selected_service && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1,
                                            borderRadius: 1.5,
                                            bgcolor: alpha(theme.palette.text.secondary, 0.05),
                                            border: `1px solid ${alpha(theme.palette.text.secondary, 0.1)}`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1,
                                                bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <ServiceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                                                {lng === "ar" ? "الخدمة" : "Service"}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                                                {lng === "ar"
                                                    ? product.selected_service.name?.ar
                                                    : product.selected_service.name?.en}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default ProductInstanceCard;
